import { useEffect, useState } from "react";
import { ReactComponent as InforCircle } from "assets/images/svgs/info-circle.svg";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import AttendeeBreadcrumb, {
  AttendeBreadcrumbLink,
} from "components/breadcrumbs/AttendeeBreadcrumb";
import useUtils from "hooks/useUtils";
import useHost from "containers/host/useHooks";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import { BookMark } from "assets/images/svgs";
import MembersInterestedPopup from "./component/MembersInterestedPopup";
import { getFilePathWithBackendUrl } from "utils/helpers/common/http-methods";
import JoiningMembersPopup from "./component/JoiningMemberPopup";
import WorkshopSessionPopup from "components/popups/workshop-session";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import useAttendee from "../useHooks";
import useAuth from "containers/auth/useHooks";
import ImageCarousel from "components/image-carousel";
import ExperienceDetailHeader from "components/experience-detail-header";
import { EVENT_STATUS } from "utils/helpers/enums/shared.enum";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import useStore from "hooks/useStore";

const AttendeeExperiencesDetail = () => {
  const breadcrumbLinks: AttendeBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.attendeeHome },
    { title: "Experiences Detail", path: siteRoutes.experiencesDetailAttendee },
  ];

  const { getQueryParams } = useUtils();
  const { getEventById } = useHost();
  const { addToWaitlist, removeFromWaitlist, getWishlistedMinors } =
    useAttendee();
  const [event, setEvent] = useState<EventDTO>(new EventDTO());
  const params = getQueryParams();

  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.attendeeHome, { replace: true });
    }
  }, [isProfileCompleted, navigate]);
  const [isInterestedPopupOpen, setIsInterestedPopupOpen] = useState(false);
  const [isJoiningPopupOpen, setIsJoiningPopupOpen] = useState(false);
  const [workshopSessionModal, setWorkshopSessionModal] = useState(false);
  const [isWaitlisted, setIsWaitlisted] = useState(event?.isWaitlisted);
  const [wishlistedMinors, setWishlistedMinors] = useState<any[]>([]);
  const [isLoadingWishlist, setIsLoadingWishlist] = useState(false);

  const [
    isParentSupervisionTooltipVisible,
    setIsParentSupervisionTooltipVisible,
  ] = useState(false);

  const { getPrivacyPolicy } = useAuth();
  const [termConditionContent, setTermConditionContent] = useState<[]>([]);

  useEffect(() => {
    getPrivacyPolicy(
      { heading: "Drop off disclaimer" },
      setTermConditionContent
    );
  }, []);



  const closeModals = () => {
    setIsInterestedPopupOpen(false);
    setIsJoiningPopupOpen(false);
  };

  const getEventDataByID = async () => {
    if (params?.id) {
      await getEventById(params?.id, setEvent);
    }
  };

  useEffect(() => {
    getEventDataByID();
  }, []);

  const goToJoining = () => {
    navigate(`${siteRoutes.attendeeJoining}?id=${params?.id}`, {
      state: { subscriptions: event.eventSubscriptions },
    });
  };

  const goToHostDetail = () => {
    navigate(`${siteRoutes.attendeeHostProfileDetail}?id=${event.host.id}&hostId=${event?.hostId}`, {
      state: { event: event },
    });
  };

  const goToRatings = () => {
    navigate(`${siteRoutes.ratings}?id=${event?.id}`);
  };

  const now = new Date();
  const sessions = event.workshopSessions || [];

  // Sort sessions by startDateTime to get the actual first session
  const sortedSessions = [...sessions].sort((a, b) => 
    new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
  );

  // Start check - using first session (earliest) start date and time
  const firstSession = sortedSessions.length > 0 ? sortedSessions[0] : null;
  const firstStart = firstSession ? new Date(firstSession.startDateTime) : null;
  const hasEventStarted =
    firstStart && !isNaN(firstStart.getTime()) && now >= firstStart;

  // Sort sessions by endDateTime to get the actual last session
  const sortedByEnd = [...sessions].sort((a, b) => 
    new Date(b.endDateTime).getTime() - new Date(a.endDateTime).getTime()
  );

  // End check - using last session (latest) end date and time
  const lastSession = sortedByEnd.length > 0 ? sortedByEnd[0] : null;
  const lastEnd = lastSession ? new Date(lastSession.endDateTime) : null;
  const hasEventEnded = lastEnd && !isNaN(lastEnd.getTime()) && now >= lastEnd;

  const visibleSessions = sortedSessions.slice(0, 3);
  const hasMoreSessions = sortedSessions.length > 3;

  const handleAddWaitList = async (event: any) => {
    const { id: eventId } = event;

    const result = await confirmationPopup(
      event?.isWaitlisted
        ? "Are you sure you want to remove from the waitlist?"
        : "Are you sure you want to add to the waitlist?"
    );

    if (!result.isConfirmed) return;

    if (event?.isWaitlisted) {
      await removeFromWaitlist(eventId);
      setEvent((prev) => ({ ...prev, isWaitlisted: false }));
    } else {
      await addToWaitlist(eventId);
      setEvent((prev) => ({ ...prev, isWaitlisted: true }));
    }
    await getEventDataByID();
  };


  useEffect(() => {
    if (event.id) {
      setIsLoadingWishlist(true);
      getWishlistedMinors(event.id, (data: any[]) => {
        setWishlistedMinors(data);
      }).finally(() => {
        setIsLoadingWishlist(false);
      });
    }
  }, [event.id]);

  return (
    <div>
      <ExperienceDetailHeader
        event={event}
        breadcrumb={<AttendeeBreadcrumb links={breadcrumbLinks} />}
        onHostClick={goToHostDetail}
        onSeeMoreSessions={() => setWorkshopSessionModal(true)}
        visibleSessions={visibleSessions}
        hasMoreSessions={hasMoreSessions}
        gradientClass="host-bg-gradient"
      />

      <div className="container my-5">
        <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-6 ">
          <div className="text-blackie text-3xl">Overview</div>
          <div className="flex gap-4">
            {hasEventEnded === true &&
              event.eventStatus === EVENT_STATUS.COMPLETED && (
                <button
                  className="px-5 py-3 flex gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
                  onClick={goToRatings}
                >
                  View Rating
                </button>
              )}

            {!hasEventStarted && !hasEventEnded && event.availableSlots > 0 && (
              <button
                className="px-5 py-3 flex gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
                onClick={goToJoining}
              >
                Join Event
              </button>
            )}

            {event.availableSlots == 0 && !hasEventStarted && !hasEventEnded && (
              <button
                className="px-5 py-3 flex gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
                onClick={() => handleAddWaitList(event)}
              >
                {event?.isWaitlisted ? "Remove from Waitlist" : "Waitlist"}
              </button>
            )}
          </div>
        </div>
        <div className="w-full flex flex-wrap md:flex-nowrap my-5 md:flex-row flex-col-reverse md:gap-6">
          <div className="w-full md:w-[60%] mt-5 md:mt-0">
            <div>
              <h3 className="text-blackie text-xl font-medium mb-2">
                About Experience
              </h3>
              <p className="text-lightBlackText text-base">
                {event?.description || "Description"}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Age Group
                </h3>
                <p className="text-lightBlackText text-base capitalize">
                  {event?.ageGroup
                    ? event.ageGroup.replace(/_/g, " ")
                    : "Age Group"}
                </p>
              </div>
              <div>
                <div className="mb-4">
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Language
                  </h3>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-fade text-blackie rounded-lg text-base">
                      {event?.language || "Language"}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Skill Level
                </h3>
                <p className="text-lightBlackText text-base capitalize">
                  {event?.skillLevel.replaceAll("_", " ") || "Skill Level"}
                </p>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-medium text-blackie mb-2">
                Details
              </h2>

              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Learning Activity
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.description || ""}
                </p>
              </div>
              {event?.specialInstructions && (
                <div className="mt-6">
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Special Instructions
                  </h3>

                  <p className="text-lightBlackText text-base">
                    {event?.specialInstructions
                      ? event.specialInstructions
                      : ""}
                  </p>
                </div>
              )}
              {event?.physicalConsideration && (
                <div className="mt-6">
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Physical Consideration
                  </h3>

                  <ul className="text-lightBlackText text-base list-disc list-inside">
                    {event?.physicalConsideration
                      ? event.physicalConsideration
                      : ""}
                  </ul>
                </div>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-2  xl:grid-cols-3 gap-6 my-6 ">
                <div>
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Core Concepts
                  </h3>
                  <p className="text-lightBlackText text-base capitalize">
                    {event?.coreConcepts?.length > 0
                      ? event.coreConcepts
                          .map((concept) => concept.name)
                          .join(", ")
                      : "-"}
                  </p>
                </div>
                <div className="relative">
                  {/* Title + Icon */}
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-blackie text-xl font-medium whitespace-nowrap">
                      Parent Supervision
                    </h3>
                    {!event?.parentSupervision && (
                      <span
                        className="cursor-pointer"
                        onMouseEnter={() =>
                          setIsParentSupervisionTooltipVisible(true)
                        }
                        onMouseLeave={() =>
                          setIsParentSupervisionTooltipVisible(false)
                        }
                      >
                        <InforCircle className="w-5 h-5 text-primaryBlueBg" />
                      </span>
                    )}
                  </div>

                  <p className="text-lightBlackText text-base capitalize">
                    {event?.parentSupervision
                      ? "Guardian Presence Required"
                      : "Drop Off"}
                  </p>

                  {/* Tooltip */}
                  {isParentSupervisionTooltipVisible && (
                    <div className="absolute top-10 left-0 w-[240px] p-3 bg-primaryBlueBg text-white text-xs rounded-lg shadow-md z-10 transition-all duration-200">
                      {termConditionContent?.map((item: any) => (
                        <div key={item.id} className="space-y-4">
                          <div
                            className="text-white text-xs"
                            dangerouslySetInnerHTML={{ __html: item.content }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {wishlistedMinors?.length > 0 && (
                  <div
                    onClick={() => setIsInterestedPopupOpen(true)}
                    className="flex gap-4 items-start cursor-pointer"
                  >
                    <div className="mt-2 ">
                      <BookMark />
                    </div>
                    <div>
                      <h3 className="text-blackie text-xl font-medium mb-2">
                        Family members
                      </h3>
                      <ul className="text-lightBlackText text-base list-disc list-inside">
                        Are also interested in this Event
                      </ul>
                    </div>
                  </div>
                )}

                {event?.eventSubscriptions?.length > 0 && (
                  <div
                    onClick={() => setIsJoiningPopupOpen(true)}
                    className="flex gap-4 cursor-pointer items-start"
                  >
                    <div className="mt-2 ">
                      <BookMark />
                    </div>
                    <div>
                      <h3 className="text-blackie text-xl font-medium mb-2">
                        Family members
                      </h3>
                      <ul className="text-lightBlackText text-base list-disc list-inside">
                        Are also Joining in this Event
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-blackie mb-6">
                Mission
              </h2>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Mission
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.host?.mission || "--"}
                </p>
              </div>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2 mt-3">
                  About{" "}
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.host?.about || ""}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
                <div className="">
                  <h3 className="text-blackie text-xl font-medium mb-2 mt-3">
                    Business Name
                  </h3>

                  <p className="text-lightBlackText text-base">
                    {event?.host?.businessName || ""}
                  </p>
                </div>
                {event?.host?.businessWebsite && (
                  <div className="">
                    <h3 className="text-blackie text-xl font-medium mb-2 mt-3">
                      Business website
                    </h3>

                    <p className="text-lightBlackText text-base">
                      {event?.host?.businessWebsite || ""}
                    </p>
                  </div>
                )}
              </div>
              <div>
                {event?.rsvp && (
                  <div className="flex flex-col items-center  justify-center gap-2 rounded-lg w-[193px] h-[217px] bg-white p-3 shadow-md">
                    <img
                      className="w-[110px] h-[110px] rounded-full object-cover"
                      src={getFilePathWithBackendUrl(
                        (event as any)?.rsvp?.profilePicUrl?.fileDownloadUri
                      )}
                      alt="RSVP"
                    />
                    <div className="text-center">
                      <p className="font-medium text-lg text-blackie">RSVP</p>
                      <p className=" text-blackie text-base">{`${event.rsvp.firstName} ${event.rsvp.lastName}`}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="w-full md:w-[40%] ">
            <div className="flex flex-col">
              <h3 className="text-blackie text-xl font-medium mb-4">
                Event Images
              </h3>
              <ImageCarousel
                images={event?.eventPicsUrl || []}
                getImagePath={getFilePathWithBackendUrl}
                height="h-[250px] lg:h-[300px]"
                autoPlayInterval={6000}
                showIndicators={true}
              />
            </div>
          </div>
        </div>
      </div>
      {isInterestedPopupOpen && (
        <MembersInterestedPopup
          isOpen={isInterestedPopupOpen}
          onClose={closeModals}
          wishlistData={wishlistedMinors || []}
          isLoading={isLoadingWishlist}
        />
      )}
      {isJoiningPopupOpen && (
        <JoiningMembersPopup
          isOpen={isJoiningPopupOpen}
          onClose={() => setIsJoiningPopupOpen(false)}
          subscriptions={event.eventSubscriptions}
          eventID={event?.id}
          refreshEvent={getEventDataByID}
          eventData={event}
        />
      )}

      {workshopSessionModal && (
        <WorkshopSessionPopup
          isOpen={workshopSessionModal}
          onClose={() => setWorkshopSessionModal(false)}
          sessions={sortedSessions}
        />
      )}
    </div>
  );
};

export default AttendeeExperiencesDetail;
